/**
 * @file NewAdminView.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import React from "react";
import { Avatar, Button, Checkbox, FormControlLabel, Grid, IconButton, Paper, Stack, Typography } from "@mui/material";
import { Box } from "@mui/material";
import { Container } from "@mui/system";
import { ThemeProvider } from "@mui/material/styles";
import { BackDropLoadingView } from "../../../utils/BackDropLoadingView";
import { AdminAddUserButton, AdminSubTitle, NewAdminTitle } from "../themes/AddNewAdminDialogTheme";
import "../../../common/css/Common.css"
import { BaseComponent } from "../../base/BaseComponent";
import { Clinic } from "../../../../services/models/Clinic";
import { NewAdminViewModel } from "../viewmodels/NewAdminViewModel";
import { TextFieldWrapper } from "../../../common/customviews/TextFieldWrapper";
import { AppUtility } from "../../../utils/AppUtility";
import { GenericResponse } from "../../../../services/models/GenericResponse";
import { ApiError } from "../../../../services/models/ApiError";
import { AdminRequest } from "../../../../services/models/AdminRequest";
import { AppAlert } from "../../../utils/AppAlert";
import { roboto500Text16 } from "../../../utils/theme/CommonTheme";
import "./NewAdminView.css"


/**
 * A type of Component state
 */
type CompState = {
    errorToggle: boolean, backDropLoading: boolean, submitted: boolean, errorMessage: string,
    clinicId: string, firstname: string, lastname: string, email: string, addNewUserEnable : boolean, isBscUser : boolean
};

/**
 * An interface definition for Component Props
 */
interface Props {
    onDialogDismissed: any,
    onNewAdminCreated: any,
    viewModel: NewAdminViewModel,
}

/**
 * A class definition for component NewAdminView
 */
export class NewAdminView extends BaseComponent<Props, CompState> {

    constructor(props: Props) {
        super(props)

        const role = this.getLoggedInUserRole();
        this.logger.verbose(`NewAdminView(Role- ${role})`, NewAdminView.name);
        
        const clinic = this.getSelectedClinic(); 
        let selectedClinic: Clinic = new Clinic();
        if(!this.isEmpty(clinic)){
           selectedClinic = JSON.parse(clinic) as Clinic; // eslint-disable-line
           this.logger.verbose(`NewAdminView(SelectedClinic- ${JSON.stringify(selectedClinic)})`, NewAdminView.name);
        }

        this.state = {
            errorToggle: false, backDropLoading: false, addNewUserEnable: false,
            submitted: false, firstname : "", lastname : "",  errorMessage: "", email : "", isBscUser : true, 
            clinicId: selectedClinic.id !! // eslint-disable-line
        };
    }

    /**
     * Further delegates dismiss event
     */
    private dismissDialog = () => {
        this.props.onDialogDismissed();
    }

    
    /**
     * Hides loading
     */
    private hideLoading = (message: string)=> {
        this.setState({
            errorMessage: message,
            errorToggle: true,
            backDropLoading: false
        });
    }

    
    /**
     * Shows loading
     */
    private showLoading = ()=>{
        this.setState({
            errorToggle: false,
            backDropLoading: true
        })
    }

    /**
     * Subscribes events
     */
    private subscribeApiEvenets = () =>{
        
        this.props.viewModel.onCreatedAdmin.subscribe((response: GenericResponse)=>{
            this.setState({
                errorToggle: false,
                backDropLoading: false,
            });
            this.logger.verbose(`createAdmin() - ${JSON.stringify(response)}`, NewAdminView.name);
            this.props.onNewAdminCreated(response)
            this.props.onDialogDismissed();
        });

        this.props.viewModel.onCreatedAdminError.subscribe((apiError: ApiError)=> {
            this.logger.error(`createAdmin() - ${JSON.stringify(apiError)}`, NewAdminView.name);
            this.hideLoading(`${apiError.message}`);
        });

    }

    /**
     * Activates admins
     * @returns 
     */
    private createAdmin = async () => {
            const accessToken = this.validateAndGetSession(NewAdminView.name);
            this.showLoading();
            const adminRequest = new AdminRequest(this.state.clinicId, this.state.email, this.state.firstname, this.state.lastname);
            this.props.viewModel.createAdmin(adminRequest, this.state.isBscUser, accessToken);
    }

    componentDidMount(): void {
        this.subscribeApiEvenets();
    }

    componentWillUnmount(): void {
        try {
            this.props.viewModel.onCreatedAdmin.unsubscribe();
            this.props.viewModel.onCreatedAdminError.unsubscribe();
            this.props.viewModel.onCreatedAdmin.unsubscribe();
            this.props.viewModel.onCreatedAdminError.unsubscribe();
        } catch(e: unknown) {
            this.logger.warning(JSON.stringify(e), NewAdminView.name);
        }
    }
    
    /**
     * Handles backdrop closing
     */
    private handleBackDropClose = () => {
        this.setState({
            backDropLoading: false
        })
    };

    /**
     * Calls on change of email values
     * @param email string
     */
    private onChangeEmail = (email: string) => {
        const isValidEmail = AppUtility.validateEmail(email);
        const enabled = isValidEmail && this.state.firstname.length > 0 && this.state.lastname.length > 0;
        this.setState({ email: email.trim(), addNewUserEnable: enabled })
    }

    /**
     * Calls on change of firstname values
     * @param email string
     */    
    private onChangeFirstName = (firstname: string) => {
        const isValidEmail = AppUtility.validateEmail(this.state.email);
        const enabled = isValidEmail && firstname.trim().length > 0 && this.state.lastname.length > 0;
        this.setState({ firstname: firstname.trim(), addNewUserEnable: enabled })
    }

    /**
     * Calls on change of firstname values
     * @param lastname string
     */
    private onChangeLastName = (lastname: string) => {
        const isValidEmail = AppUtility.validateEmail(this.state.email);
        const enabled = isValidEmail && this.state.firstname.length > 0 && lastname.trim().length > 0;
        this.setState({ lastname: lastname.trim(), addNewUserEnable: enabled })
    }

    render() {
        return <>
            <div>
                <Box
                    sx={{
                        display: "flex",
                        "& > :not(style)": {
                            m: 1,
                            width: "100%"
                        },
                    }}>
                    <Paper className="NewAdminBoxContainer" elevation={0}>
                        <Grid container spacing={0} >
                            <Grid item xs={9}>
                                <ThemeProvider theme={NewAdminTitle}>
                                    <Typography className="TitleUniqueId">
                                        Add a new user
                                    </Typography>
                                </ThemeProvider >
                            </Grid>
                            <Grid item xs={3} container justifyContent="flex-end">
                            <IconButton className="CloseButton" onClick={this.dismissDialog}><Avatar src={"/../closeIcon.svg"} /></IconButton>                            
                        </Grid>
                        </Grid>
                            
                        <ThemeProvider theme={AdminSubTitle}>
                            <Typography className="NewDialogSubTitle">
                                Enter a new user’s details below. They will be prompted to set a password the first time they login. Notice, the invite is valid for 24 hours.
                            </Typography>
                        </ThemeProvider>
                        <Container>
                                <Stack
                                                direction="row"
                                                justifyContent="flex-end"
                                                alignItems="center"
                                                spacing={2} sx={{height: "10vh"}}>
                                    <ThemeProvider theme={roboto500Text16}>
                                        <TextFieldWrapper size="medium" fullWidth id="first-name" label="Enter first name" variant="outlined" autoFocus
                                            className="NewAdminTextField" inputProps={{ maxLength: 15, style: { textTransform: 'capitalize'} }} onChange={(e) => { this.onChangeFirstName(e.target.value) }}/>
                                        <TextFieldWrapper size="medium" fullWidth id="last-name" label="Enter last name" variant="outlined"
                                            className="NewAdminTextField"  inputProps={{ maxLength: 11, style: { textTransform: 'capitalize'} }} onChange={(e) => { this.onChangeLastName(e.target.value) }}/>
                                    </ThemeProvider>    
                                </Stack>
                                <Stack
                                                direction="row"
                                                justifyContent="flex-start"
                                                alignItems="center"
                                                spacing={2} sx={{height: "10vh", marginTop:"5vh"}}>
                                    <ThemeProvider theme={roboto500Text16}>            
                                        <TextFieldWrapper id="email-address" label="Enter work email address" variant="outlined"
                                                className="NewAdminTextFieldEmail" onChange={(e) => { this.onChangeEmail(e.target.value) }}/>
                                    </ThemeProvider>
                                        <FormControlLabel className="BscRepCheckbox" control={<Checkbox checked= {this.state.isBscUser} onClick={ ()=> this.setState( { isBscUser : !this.state.isBscUser})}/>} label="Bsc Rep"/>
                                </Stack>
                                <Stack
                                        direction="row"
                                        justifyContent="flex-end"
                                        alignItems="flex-end" sx={{marginTop: "0vh"}}
                                        spacing={2}
                                        >
                                        <ThemeProvider theme={AdminAddUserButton}>
                                            <Button variant="contained" onClick={this.dismissDialog} size="small"
                                                disableElevation className="CancelNewDialogBtn" >
                                                Cancel
                                            </Button>
                                        </ThemeProvider>
                                        <ThemeProvider theme={AdminAddUserButton}>
                                            <Button variant="contained" onClick={this.createAdmin} size="small"
                                                disableElevation className="ActivateNewUserBtn" disabled={!this.state.addNewUserEnable}>
                                                Add User
                                            </Button>
                                    </ThemeProvider>
                            </Stack>
                        </Container>
                            
                    </Paper>
                </Box>
                <BackDropLoadingView handleBackDropClose={this.handleBackDropClose} isLoading={this.state.backDropLoading} />
                {
                    this.state.errorToggle && <AppAlert severity="error">
                        {this.state.errorMessage}
                    </AppAlert>
                }
            </div>
        </>
    }

}